import { useState, useRef } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { createScenePreviewJob, waitForJobCompletion, type ScenePreviewResult } from '../../../../services/asyncJobApi';
import { calculateAspectRatio } from '../../../materials/shared/materialUtils';
import { useUpdateScene } from '../../../../features/scenarios/hooks';
import {
  DEFAULT_GENERATION_MODEL,
  thinkingLevelPayload,
  useGenerationState,
  type SelectedCasting,
  type CastingActor,
} from '../../state/useGenerationState';
import type { ScenarioFormState } from './useScenarioFormState';
import type { UseProductSelectionReturn } from './useProductSelection';
import type { DroppedMaterial, Scenario, ScenarioLocation, ScenarioResponse } from '../types';
import {
  extractAsinsFromAssets,
  isFaceSafeSeedanceScene,
  deriveSceneExtendMode,
  effectiveFrameLinkPreviousVideo,
  deriveSceneInputFamily,
  deriveSceneEffectiveVideoGenerationMode,
} from '../utils';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import { resolveErrorMessage } from '../../../../utils/getErrorMessage';
import { useSceneStateContext } from './SceneStateContext';
import { isStorageQuotaError, storageQuotaErrorMessage } from '../../../../utils/storageQuotaError';
import { notifyJobComplete } from '../../../../utils/jobNotifications';
import { isCharacterActor } from '../../../../features/actors/actorAttributes';
import { GENERATED_SCENE_FRAME_SOURCE_TYPE, isGeneratedSceneFrameAssetId } from '../../state/assetMediaUtils';
import { addToPromptHistory, PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import type { SceneFrameSettings } from './sceneFrameSettingsQueue';
import { buildSceneFrameReferenceCandidates } from '../utils/sceneFrameReferences';
import { fetchMaterialById } from '../../../../features/materials/api';
import { resolveMaterialUrls } from '../../../../features/materials/materialUrl';
import { AddedMaterialCache } from './addedMaterialCache';

interface UseFrameHandlersParams {
  // State
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  scenarios: Scenario[];

  /** シーン単位のフレーム生成設定の flush (保存済みの最新値を返す。useSceneFrameSettings) */
  flushSceneFrameSettings: (scene: { id: string }) => Promise<SceneFrameSettings>;
  setExpandedConfirmButton: React.Dispatch<React.SetStateAction<string | null>>;
  processedFirstFrameAssets: React.MutableRefObject<Set<string>>;

  /** シナリオ設定フォーム一式 (useScenarioFormState の返り値)。 */
  form: ScenarioFormState;
  /** 商品入力フォーム一式 (useProductSelection の返り値)。 */
  product: UseProductSelectionReturn;

  // Credit/storage checks
  isCreditInsufficient: boolean;
  isStorageExceeded: boolean;
  openPurchaseModal: () => void;

  // Other
  tempMaterials: { refetch: () => Promise<any> };
  refetchCredits: () => void;
}

/** フレーム生成のキャスティング素材と settings を全AI俳優(主+追加)から組み立てる(純粋関数)。
 * stylized (非写実系) では写実前提の人間俳優を使わないが、キャラクター俳優
 * (ethnicity='character') は全スタイルで選択可 (計画書§4 P5-1)。CastingSection の
 * ゲートにより非写実系×非Seedanceでは人間俳優自体が選択できない不変条件があるが、
 * 念のためここでも同じ判定で絞り込む (fail-safe)。materials は登場人物A/B…順の
 * キャラクターシート。settings.model_ids は順序付き全選択(metadata + frame_generator
 * 側の並べ替え正本)。 */
function buildFrameCasting(
  selectedCasting: SelectedCasting,
  additionalCastings: CastingActor[],
  visualStyleIsStylized: boolean,
): { materials: { id: string; sourceUrl: string }[]; settings: { model_ids: string[] } | undefined } {
  const fullCast = [...(selectedCasting ? [selectedCasting] : []), ...additionalCastings]
    .filter((c) => !visualStyleIsStylized || isCharacterActor(c));
  if (fullCast.length === 0) return { materials: [], settings: undefined };
  const materials = fullCast
    .filter((c) => c.character_sheet_public_url)
    .map((c) => ({ id: `casting-${c.id}`, sourceUrl: c.character_sheet_public_url as string }));
  return { materials, settings: { model_ids: fullCast.map((c) => c.id) } };
}

/** 場面固有化 (P1 step4): シナリオの場面宣言 (`locations`) を解決する。
 *  シナリオ読み込み (useScenarioGeneration の handleLoadScenario) 経由では
 *  `scenario.locations` (generation_metadata から復元済み) を使うが、生成直後は
 *  URL effect が `handleLoadScenario` を意図的にスキップするため (直前に作った
 *  シナリオの再フェッチによる商品画像 auto-complete の誤発火防止)、`lastResult` は
 *  生成 API の素のレスポンスのままで `locations` は `generation_metadata.locations`
 *  にしか無い。両方をフォールバックで見て、生成直後にフレーム生成した場合でも
 *  場面一貫性指示 (build_scene_intro_prompt) が抜け落ちないようにする。 */
function resolveScenarioLocations(scenario: Scenario): ScenarioLocation[] | undefined {
  return scenario.locations
    ?? (scenario.generation_metadata as { locations?: ScenarioLocation[] } | undefined)?.locations;
}

export function useFrameHandlers(params: UseFrameHandlersParams) {
  const {
    lastResult,
    setLastResult,
    scenarios,
    flushSceneFrameSettings,
    setExpandedConfirmButton,
    processedFirstFrameAssets,
    isCreditInsufficient,
    isStorageExceeded,
    openPurchaseModal,
    tempMaterials,
    refetchCredits,
  } = params;

  // 生成設定 (シナリオ設定フォーム)
  const {
    scenarioGenerationModel,
    thinkingLevel,
    tone,
    duration,
  } = params.form;

  // 商品情報
  const { manualProductId, selectedProduct } = params.product;

  // 生成ステート (以前は親から props で受け取っていたが同じ Context を直接読む)
  const {
    activeScenarioId,
    videoSettings,
    avoidFaces,
    visualStyle,
    shootingMethod,
    visualStyleIsStylized,
    selectedAssets,
    selectedCasting,
    additionalCastings,
    removeAsset,
    removeAssetFromScope,
    addAssetToScope,
    getAssetsForScope,
    requestOpenPersonalFolder,
    setActiveJobs,
    openFrameNotifyBatch,
    closeFrameNotifyBatch,
  } = useGenerationState();

  const {
    scenePreviews, setScenePreviews,
    scenePreviewIndex, setScenePreviewIndex,
    scenePreviewMaterials, setScenePreviewMaterials,
    setScenePreviewLoading, setScenePreviewGenerated,
    confirmedFirstFrames, setConfirmedFirstFrames,
    sceneLastFrames, setSceneLastFrames, sceneLastFramesRef,
    setLastFrameLoading,
  } = useSceneStateContext();
  const toast = useToast();
  const updateSceneMutation = useUpdateScene();
  const { data: videoModelsForLink = [] } = useGenerationModels('video');

  // 延長 (「前の動画とつなげる」) が実効 ON のシーンか (フレーム家系限定。
  // docs/features/link-previous-scene-continuity-default-plan.md §9 step-1 項目5)。
  // シーン単位のモデル上書き (useVideoHandlers の sceneVideoSettings) はここでは見ず、
  // シナリオ既定モデル (videoSettings.videoModel) で近似する
  // (フレーム自動生成のスキップ可否の判定には十分。動画 payload・課金見積りの正本は
  // useVideoHandlers.ts / SceneItem.tsx 側の frameLinkActive)。
  // P2 step 4: シナリオ単位の videoGenerationMode (context) の代わりにシーン単位の
  // deriveSceneInputFamily を使う (video_input_mode が全 NULL の現在は常に同値)。
  const isFrameLinkPreviousActive = (scenario: Scenario, scene: Scenario['scenes'][number]): boolean => {
    const sceneFamily = deriveSceneInputFamily(scenario, scene);
    if (sceneFamily !== 'frame') return false;
    const stored = scene.link_previous_video;
    // 延長 (extend) の実効判定の起点は deriveSceneExtendMode に一本化済み (P2 step 2。
    // docs/features/scene-location-and-production-mode-plan.md §3.3/§3.3.1)。
    // DB 由来のシーン行は未設定を null で持つ (20260829_03: NULL=未設定・true/false=明示)。
    // `=== undefined` だと null を「明示 OFF」と誤読し、延長シーンのフレームを
    // 一括自動生成がスキップせず無駄に生成していた (UI 側の frameLinkActive は
    // local state 経由で導出に落ちるため表示は「延長」になり、乖離していた)。
    // また §3.3.1 の優先順位どおり、video_input_mode の明示値があるときは旧
    // link_previous_video の明示値より導出 (優先順位1で video_input_mode を読む) を
    // 優先する (getSceneVideoSettings と同じ手当て。2026-08-31)。
    const hasExplicitVideoInputMode = scene.video_input_mode === 'frame'
      || scene.video_input_mode === 'extend'
      || scene.video_input_mode === 'auto';
    const linkPreviousVideoSetting = (stored == null || hasExplicitVideoInputMode)
      ? deriveSceneExtendMode(scenario, scene, videoSettings.videoModel, videoModelsForLink)
      : stored;
    return effectiveFrameLinkPreviousVideo({
      videoGenerationMode: sceneFamily,
      scenario,
      scene,
      linkPreviousVideoSetting,
      videoModel: videoSettings.videoModel,
      videoModels: videoModelsForLink,
    });
  };

  // シナリオ番号とシーン番号からラベルを生成（例: "シナリオ1-2"）
  const sceneLabel = (scenario: Scenario, scene: { order: number }) => {
    const scenarioIndex = scenarios.findIndex(s => s.id === scenario.id) + 1;
    return `シナリオ${scenarioIndex}-${scene.order}`;
  };

  // Frame-specific state (batch generation)
  const [batchGeneratingFrames, setBatchGeneratingFrames] = useState<Record<string, boolean>>({});
  const batchFrameCancelRefs = useRef<Record<string, boolean>>({});
  const [showBatchCancelModal, setShowBatchCancelModal] = useState<string | null>(null);

  // 最初のフレームを確定する
  const handleConfirmFirstFrame = async (sceneId: string, sceneKey: string, materialId: string) => {
    try {
      // APIでimage_material_idを更新
      await updateSceneMutation.mutateAsync({
        sceneId,
        payload: { image_material_id: materialId },
      });

      // Update local state
      if (lastResult) {
        setLastResult((prev) => {
          if (!prev) return prev;
          const updatedScenarios = prev.scenarios.map((s) => ({
            ...s,
            scenes: s.scenes.map((sc) => {
              if (sc.id === sceneId) {
                return { ...sc, image_material_id: materialId };
              }
              return sc;
            }),
          }));
          return { ...prev, scenarios: updatedScenarios };
        });
      }

      // 旧確定の処理済みフラグをクリアし、新しい確定の追加を許可
      const oldId = confirmedFirstFrames[sceneKey];
      if (oldId) {
        processedFirstFrameAssets.current.delete(`${sceneKey}:${oldId}`);
      }
      setConfirmedFirstFrames(prev => ({ ...prev, [sceneKey]: materialId }));

      toast.success('最初のフレームを確定しました');
    } catch (error) {
      console.error('Failed to confirm first frame:', error);
      toast.error('確定に失敗しました');
    }
  };

  // 最初のフレームの確定を解除する（currentIndexが指定された場合はそのフレームのみ削除）
  // フレームを外すときの選択中素材の片付けは生成フレームに限る
  // (ユーザーが選んだ素材をドラッグで置いた場合は選択中素材に残す。isGeneratedSceneFrameAssetId 参照)
  const removeGeneratedFrameAsset = (id: string) => {
    if (isGeneratedSceneFrameAssetId(selectedAssets, id)) removeAsset(id);
  };
  const removeGeneratedFrameAssetFromScope = (scope: string, id: string) => {
    if (isGeneratedSceneFrameAssetId(getAssetsForScope(scope), id)) removeAssetFromScope(scope, id);
  };

  const handleClearFirstFrame = async (sceneId: string, sceneKey: string, currentIndex?: number) => {
    try {
      const materialsForScene = scenePreviewMaterials[sceneKey] || [];
      const imagesForScene = scenePreviews[sceneKey] || [];
      const removeIndex = currentIndex ?? (scenePreviewIndex[sceneKey] || 0);
      const currentMaterial = materialsForScene[removeIndex];
      const isLastOne = materialsForScene.length <= 1;

      // 複数フレームがあり、1つだけ削除する場合
      if (!isLastOne) {
        // 該当素材のみ選択中素材から除外
        if (currentMaterial?.id) removeGeneratedFrameAsset(currentMaterial.id);

        // プレビュー画像・素材から該当インデックスのみ削除
        const newImages = imagesForScene.filter((_: string, i: number) => i !== removeIndex);
        const newMaterials = materialsForScene.filter((_: any, i: number) => i !== removeIndex);

        setScenePreviews(prev => ({ ...prev, [sceneKey]: newImages }));
        setScenePreviewMaterials(prev => ({ ...prev, [sceneKey]: newMaterials }));

        // インデックスを調整（削除後に範囲外にならないように）
        const newIndex = removeIndex >= newMaterials.length ? newMaterials.length - 1 : removeIndex;
        setScenePreviewIndex(prev => ({ ...prev, [sceneKey]: Math.max(0, newIndex) }));

        // 削除対象が確定済みの場合、次（右）のフレームを自動確定
        const isConfirmed = confirmedFirstFrames[sceneKey] === currentMaterial?.id;
        if (isConfirmed) {
          // newMaterials[newIndex] が削除後の「次」のフレーム（右側にあったもの）
          const nextMaterial = newMaterials[Math.max(0, newIndex)];
          if (nextMaterial?.id) {
            await updateSceneMutation.mutateAsync({
              sceneId,
              payload: { image_material_id: nextMaterial.id },
            });

            setLastResult(prev => {
              if (!prev) return prev;
              const updatedScenarios = prev.scenarios.map((s) => ({
                ...s,
                scenes: s.scenes.map((sc) => {
                  if (sc.id === sceneId) {
                    return { ...sc, image_material_id: nextMaterial.id };
                  }
                  return sc;
                }),
              }));
              return { ...prev, scenarios: updatedScenarios };
            });

            // 旧確定の処理済みフラグをクリアし、新しい確定を設定
            processedFirstFrameAssets.current.delete(`${sceneKey}:${currentMaterial.id}`);
            setConfirmedFirstFrames(prev => ({ ...prev, [sceneKey]: nextMaterial.id }));
          }
        }

        toast.success('フレームを削除しました');
        return;
      }

      // 最後の1つを削除する場合（従来通り全クリア）
      await updateSceneMutation.mutateAsync({
        sceneId,
        payload: { image_material_id: undefined },
      });

      setLastResult(prev => {
        if (!prev) return prev;
        const updatedScenarios = prev.scenarios.map((s) => ({
          ...s,
          scenes: s.scenes.map((sc) => {
            if (sc.id === sceneId) {
              return { ...sc, image_material_id: undefined };
            }
            return sc;
          }),
        }));
        return { ...prev, scenarios: updatedScenarios };
      });

      // 選択中素材から除外
      materialsForScene.forEach((m: any) => {
        if (m?.id) removeGeneratedFrameAsset(m.id);
      });

      // 確定済み状態を削除
      setConfirmedFirstFrames(prev => {
        const next = { ...prev };
        delete next[sceneKey];
        return next;
      });

      // プレビュー画像をクリア（プレースホルダに戻す）
      setScenePreviews(prev => {
        const next = { ...prev };
        delete next[sceneKey];
        return next;
      });

      // プレビュー素材をクリア
      setScenePreviewMaterials(prev => {
        const next = { ...prev };
        delete next[sceneKey];
        return next;
      });

      // プレビューインデックスを0にリセット
      setScenePreviewIndex(prev => {
        const next = { ...prev };
        delete next[sceneKey];
        return next;
      });

      toast.success('最初のフレームをクリアしました');
    } catch (error) {
      console.error('Failed to clear first frame:', error);
      toast.error('クリアに失敗しました');
    }
  };

  // シーンプレビュー（最初のフレーム）を生成する
  /** 場面転換シーンのフレーム生成にだけ渡す内部参照 (直前の確定フレーム)。
   *  (docs/features/link-previous-scene-continuity-default-plan.md §9 step-1 項目9)
   *  - 正本 = DB の scenes.image_material_id (画面上の confirmedFirstFrames は生成直後の未保存期間のフォールバック)
   *  - 「直前シーン」= ブランドカットを除いた本編の直前 (findPreviousVideoLastFrame と同じ遡り規則)
   *  - 素材が削除済み・URL 解決不能なら内部参照なしで続行 (fail-soft)
   *  - selectedAssets には一切追加しない (素材欄への露出・動画生成への混入防止) */
  const findInternalReferenceMaterial = (
    scenario: Scenario, scene: Scenario['scenes'][number], isEndCard: boolean,
  ): { id: string; sourceUrl: string } | null => {
    if (deriveSceneInputFamily(scenario, scene) !== 'frame' || isEndCard) return null;
    if (scene.continues_previous_shot) return null; // 場面継続は延長がカバーする
    const preceding = (scenario.scenes || [])
      .filter((s) => (
        s.brand_cut_position !== 'start'
        && s.brand_cut_position !== 'end'
        && s.order < scene.order
      ))
      .sort((a, b) => b.order - a.order);
    const prevScene = preceding[0];
    if (!prevScene) return null;
    const prevKey = `${scenario.id}-scene-${prevScene.order}`;
    const materialId = prevScene.image_material_id || confirmedFirstFrames[prevKey] || null;
    if (!materialId) return null;
    const prevMaterials = scenePreviewMaterials[prevKey] || [];
    const match = prevMaterials.find((m: any) => m?.id === materialId);
    const url = match?.source_url || match?.sourceUrl || match?.preview_url || match?.previewUrl;
    if (!url) return null;
    return { id: materialId as string, sourceUrl: url as string };
  };

  /** 参照画像の候補 (scene-frame-generation-tuning-plan.md §6.2)。生成と調整ダイアログ (ドライラン) が
   *  同じ入力を使うので、一覧の「添付予定」と実際の添付が一致する。添付の決定 (include_product の既定・
   *  上限・hero・重複統合・本文メンション) は BE の計画が行う。 */
  // このシーンだけ追加した素材 (overrides.added) の URL / 名前。候補関数は同期なので、生成・ドライランの前に
  // ensureAddedMaterialsResolved で埋めておく。解決できない (削除済み・画像でない) ものは url=null で unresolved になる
  const addedMaterialCacheRef = useRef<AddedMaterialCache | null>(null);
  if (addedMaterialCacheRef.current === null) {
    addedMaterialCacheRef.current = new AddedMaterialCache(async (id) => {
      const material = await fetchMaterialById(id);
      if (!material) return null;
      const contentType = String((material as { content_type?: string }).content_type || '');
      const urls = resolveMaterialUrls(material as never);
      // 画像以外は添付しない。SVG 原本はラスタ版だけを AI に渡す (buildDragData と同じ扱い)
      const url = !contentType.startsWith('image/')
        ? null
        : (contentType === 'image/svg+xml' ? (urls.raster || null) : (urls.source || urls.raster || null));
      return { url, name: (material as { name?: string | null }).name ?? null };
    });
  }
  const ensureAddedMaterialsResolved = (ids: ReadonlyArray<string>): Promise<void> =>
    addedMaterialCacheRef.current!.ensure(ids);
  const forgetAddedMaterial = (id: string): void => {
    addedMaterialCacheRef.current!.forget(id);
  };

  const collectSceneFrameReferenceCandidates = (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
    frameType: 'first' | 'last',
    opts?: { additive?: boolean; firstFrame?: { id: string | null; url: string } | null; added?: ReadonlyArray<string> },
  ) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    const previousMaterialIds = new Set<string>(
      (scenePreviewMaterials[key] || []).map((m: any) => m?.id).filter(Boolean),
    );
    const shouldIncludeProduct = scene.include_product ?? true;
    const isEndCard = !!(scene.preset_image_material_id && scene.preset_image_url) && !shouldIncludeProduct;
    const casting = [...(selectedCasting ? [selectedCasting] : []), ...additionalCastings]
      .filter((c) => !visualStyleIsStylized || isCharacterActor(c));
    return buildSceneFrameReferenceCandidates({
      frameType,
      scene,
      selectedAssets,
      previousMaterialIds: opts?.additive ? undefined : previousMaterialIds,
      internalReference: frameType === 'first' ? findInternalReferenceMaterial(scenario, scene, isEndCard) : null,
      firstFrame: opts?.firstFrame ?? null,
      casting,
      addedMaterials: (opts?.added ?? []).map((id) => {
        const cached = addedMaterialCacheRef.current!.get(id);
        return { id, url: cached?.url ?? null, name: cached?.name ?? null };
      }),
    });
  };

  const handleGenerateScenePreview = async (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
    options?: { waitForCompletion?: boolean; silent?: boolean; additive?: boolean; notifyBatchId?: string }
  ) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    const frameMode = scenario.frame_mode || 'first_only';
    // 一括生成から呼ばれた場合は notifyBatchId が渡る。各フレームジョブにこの ID を
    // 付与しておくと、集約点で個別完了通知が抑止され、まとめ通知を一括生成ループの
    // 完了時に 1 本だけ出せる (バッチ消失時は個別通知へフォールバック)。
    const notifyFields = options?.notifyBatchId ? { notifyBatchId: options.notifyBatchId } : {};

    if (isCreditInsufficient) {
      openPurchaseModal();
      return;
    }

    if (isStorageExceeded) {
      toast.error(storageQuotaErrorMessage());
      return;
    }

    // シーン単位のフレーム生成設定 (追加プロンプト)。保存済みの最新値を正本にし、保留中の保存を
    // 待ってから組む (クロージャの scene は古いことがある。scene-frame-generation-tuning-plan.md §6.7)
    let frameSettings: SceneFrameSettings;
    try {
      frameSettings = await flushSceneFrameSettings(scene);
    } catch (err) {
      if (options?.waitForCompletion) throw err;
      toast.error('フレーム生成の調整を保存できていないため、生成を始められません');
      return;
    }
    const frameAdditionalPrompt = frameSettings.additionalPrompt.trim();

    // faceSafe シーン (Seedance×フレーム家系×写実系×顔あり×セクション開始):
    // フレームを絵コンテ用イラストとして生成する (要求値。最終判定はバックエンド §4.2)。
    // イラストフレームは i2v に使えないため、last frame 系の生成・連鎖からも除外する。
    // P2 step 4: シナリオ単位の videoGenerationMode の代わりにシーン単位の
    // deriveSceneInputFamily を使う (video_input_mode が全 NULL の現在は常に同値)。
    const sceneIsFirst = scenario.scenes[0]?.id === scene.id;
    const sceneFamilyForPreview = deriveSceneInputFamily(scenario, scene);
    const faceSafeIllustration = isFaceSafeSeedanceScene(
      videoSettings.videoModel, sceneFamilyForPreview, avoidFaces,
      sceneIsFirst, scene.continues_previous_shot ?? false,
      scene.has_visible_face, visualStyleIsStylized,
    );

    // continues_previous_shot: 前のシーンの最後のフレームを最初のフレームとして流用（first_and_lastモードのみ）
    if (frameMode === 'first_and_last' && scene.continues_previous_shot && scene.order > 1) {
      const prevScene = scenario.scenes.find(s => s.order === scene.order - 1);
      if (prevScene) {
        const prevKey = `${scenario.id}-scene-${prevScene.order}`;
        // refから最新値を読む（一括生成時、クロージャのstateは古いため）
        const prevLastFrame = sceneLastFramesRef.current[prevKey];
        if (prevLastFrame?.url) {
          // 前シーンの最後のフレームを最初のフレームとして設定
          setScenePreviews((prev) => ({
            ...prev,
            [key]: [prevLastFrame.url],
          }));
          setScenePreviewIndex(prevIndex => ({ ...prevIndex, [key]: 0 }));
          setScenePreviewGenerated((prev) => ({ ...prev, [key]: true }));
          // エクスポート用にconfirmedFirstFramesを設定（collectScenarioDownloadDataで参照される）
          if (prevLastFrame.materialId) {
            setConfirmedFirstFrames(prev => ({ ...prev, [key]: prevLastFrame.materialId }));
          }
          if (!options?.silent) {
            toast.success(`${sceneLabel(scenario, scene)}は前のシーンと同一構図のため、最後のフレームを流用しました`);
          }

          // シーンのimage_url/image_material_idをDBに保存（シナリオ再読み込み時の復元用）
          try {
            await updateSceneMutation.mutateAsync({
              sceneId: scene.id,
              payload: {
                image_url: prevLastFrame.url,
                image_material_id: prevLastFrame.materialId || undefined,
              },
            });
          } catch (err) {
            console.error('フレーム流用時のシーン更新に失敗:', err);
          }

          // first_and_lastモードの場合、最後のフレームも生成
          const sceneSetting = scene.setting || '';
          const isEndCardScene = sceneSetting === 'エンドカード' || sceneSetting === '商品カット';
          if (frameMode === 'first_and_last' && !isEndCardScene) {
            try {
              setLastFrameLoading(prev => ({ ...prev, [key]: true }));

              // 最後のフレーム: 流用した最初のフレームを基準に、単独生成と同じ候補関数で参照を組む (§6.2)
              const shouldIncludeProductLF = scene.include_product ?? true;
              const frameCastingLF = buildFrameCasting(selectedCasting, additionalCastings, visualStyleIsStylized);
              await ensureAddedMaterialsResolved(frameSettings.overrides?.added ?? []);
              const lastReferencePlanInputLF = {
                candidates: collectSceneFrameReferenceCandidates(scenario, scene, 'last', {
                  firstFrame: { id: prevLastFrame.materialId || null, url: prevLastFrame.url },
                  added: frameSettings.overrides?.added ?? [],
                }),
                overrides: frameSettings.overrides,
                frame_type: 'last' as const,
              };
              const asinLF = shouldIncludeProductLF ? extractAsinsFromAssets(selectedAssets)[0] : undefined;
              const hasProductAssetsLF = selectedAssets.some(a => a.hasProductLinks);
              const productIdLF = shouldIncludeProductLF && hasProductAssetsLF ? (manualProductId || selectedProduct?.id) : undefined;
              const productTypeLF = productIdLF ? (manualProductId ? 'manual' : 'amazon') : undefined;

              const lastFrameJob = await createScenePreviewJob({
                scene_id: scene.id,
                scene: { ...scene, include_product: shouldIncludeProductLF },
                scenario: {
                  title: scenario.title,
                  scenes: scenario.scenes.map(s => ({ order: s.order, visual: s.visual, setting: s.setting, location_key: s.location_key })),
                  locations: resolveScenarioLocations(scenario),
                },
                reference_plan_input: lastReferencePlanInputLF,
                asin: asinLF,
                product_id: productIdLF,
                product_type: productTypeLF,
                model: scenarioGenerationModel || DEFAULT_GENERATION_MODEL,
                thinking_level: thinkingLevelPayload(scenarioGenerationModel || DEFAULT_GENERATION_MODEL, thinkingLevel),
                aspect_ratio: videoSettings.aspectRatio,
                resolution: videoSettings.resolution,
                prompt_options: {
                  tone: tone || undefined,
                  duration: duration || undefined,
                },
                settings: frameCastingLF.settings,
                visual_style: visualStyle,
                shooting_method: shootingMethod,
                avoid_faces: (!visualStyleIsStylized && avoidFaces) || undefined,
                frame_type: 'last',
              });

              refetchCredits();

              setActiveJobs(prev => [...prev, {
                jobId: lastFrameJob.job_id,
                status: 'polling' as const,
                startedAt: Date.now(),
                type: 'scene-preview' as const,
                label: sceneLabel(scenario, scene),
                ...notifyFields,
              }]);

              const lastFramePromise = waitForJobCompletion<ScenePreviewResult>(lastFrameJob.job_id, {
                maxDuration: 20 * 60 * 1000,
                // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
                skipVisibilityCheck: true,
              }).then(async (lastFrameResult) => {
                const lastFrameData = lastFrameResult.result_data;

                setActiveJobs(prev => prev.map(j =>
                  j.jobId === lastFrameJob.job_id ? {
                    ...j,
                    status: 'completed' as const,
                    result: {
                      id: lastFrameData.material_id || lastFrameJob.job_id,
                      material_id: lastFrameData.material_id,
                      image_url: lastFrameData.image_url,
                    },
                  } : j
                ));

                if (lastFrameData.image_url) {
                  const lastFrameMaterial = lastFrameData.material as Record<string, string | number | null | undefined> | undefined;
                  // materialからフルサイズURLを解決（lastFrameData.image_urlはサムネイル優先のため）
                  const lastFrameSourceUrl = (
                    lastFrameMaterial?.source_url ||
                    lastFrameMaterial?.sourceUrl ||
                    lastFrameMaterial?.public_url ||
                    lastFrameMaterial?.publicUrl ||
                    lastFrameMaterial?.preview_url ||
                    lastFrameMaterial?.previewUrl ||
                    lastFrameData.image_url
                  ) as string;

                  setSceneLastFrames((prev) => ({
                    ...prev,
                    [key]: {
                      url: lastFrameData.image_url!,
                      sourceUrl: lastFrameSourceUrl,
                      materialId: (lastFrameMaterial?.id as string) || '',
                    },
                  }));

                  try {
                    // DBにはサムネイルURLを保存（表示用）。フル解像度はscenario_crud.pyがmaterial_idから解決
                    await updateSceneMutation.mutateAsync({
                      sceneId: scene.id,
                      payload: {
                        last_frame_url: lastFrameData.image_url!,
                        last_frame_material_id: (lastFrameMaterial?.id as string) || undefined,
                      },
                    });
                  } catch (err) {
                    console.error('最後のフレームの保存に失敗:', err);
                  }

                  await tempMaterials.refetch();
                  refetchCredits();
                  toast.success(`${sceneLabel(scenario, scene)}の最後のフレームを生成しました`);
                }
              }).catch((err) => {
                console.error('[ScenePreview] 最後のフレーム生成に失敗:', err);
                if (isStorageQuotaError(err)) { toast.error(storageQuotaErrorMessage()); } else { toast.error(`${sceneLabel(scenario, scene)}の最後のフレーム生成に失敗しました`); }
                setActiveJobs(prev => prev.map(j =>
                  j.jobId === lastFrameJob.job_id ? { ...j, status: 'failed' as const, error: String(err) } : j
                ));
                refetchCredits();
              }).finally(() => {
                setLastFrameLoading(prev => ({ ...prev, [key]: false }));
              });

              // waitForCompletion時は最後のフレーム完了も待つ（一括生成で次シーンが前シーンの最後のフレームを参照するため）
              if (options?.waitForCompletion) {
                await lastFramePromise;
              }
            } catch (lastFrameError) {
              console.error('[ScenePreview] 最後のフレームジョブ作成に失敗:', lastFrameError);
              toast.error(`${sceneLabel(scenario, scene)}の最後のフレーム生成ジョブの作成に失敗しました`);
              setLastFrameLoading(prev => ({ ...prev, [key]: false }));
            }
          }

          return;
        }
      }
    }

    // 同じシーンで以前生成した素材のIDリストを取得
    const previousMaterialsForScene = scenePreviewMaterials[key] || [];
    const previousMaterialIds = new Set(previousMaterialsForScene.map((m: any) => m.id).filter(Boolean));

    // additive モード（+1ボタン）では以前の素材をクリアしない
    // 再生成の場合、以前生成した素材をselectedAssetsから除外
    // （気に入らなかったから再生成するので、参考画像として使うべきではない）
    // スコープ指定版を使い、非アクティブシナリオでも正しいスコープから除外
    if (!options?.additive && previousMaterialIds.size > 0) {
      previousMaterialIds.forEach(id => {
        removeGeneratedFrameAssetFromScope(scenario.id, id);
      });
    }

    // 選択した素材から、このシーンで以前生成した素材を除外（念のためフィルタも残す）
    // additive モードでは除外しない（既存素材を参照画像として使い続ける）
    const filteredSelectedAssets = options?.additive
      ? selectedAssets
      : selectedAssets.filter(asset => !previousMaterialIds.has(asset.id));

    // 商品を描写するか (プロンプトの指示) はシナリオ生成AIの判定値 (scene.include_product) をそのまま使う
    // (商品タブは廃止。scene-frame-generation-tuning-plan.md §2.3)。参照画像を添付するかは BE の計画が決める (§6.5)
    const shouldIncludeProduct = scene.include_product ?? true;
    const sceneWithIncludeProduct = { ...scene, include_product: shouldIncludeProduct };

    // ASIN / 商品情報は「商品を描写するための情報」として送る (画像の自動補完には使わない。§6.4)。
    // state 変数ではなく選択素材から導出する (✕で素材を外しても state が残り送信されるのを防ぐ)
    const extractedAsins = extractAsinsFromAssets(filteredSelectedAssets);
    const asinValue = shouldIncludeProduct && extractedAsins.length > 0 ? extractedAsins[0] : undefined;
    const hasProductAssetsInSelection = filteredSelectedAssets.some(a => a.hasProductLinks);
    const productIdValue = shouldIncludeProduct && hasProductAssetsInSelection
      ? (manualProductId || selectedProduct?.id)
      : undefined;
    const productTypeValue = productIdValue
      ? (manualProductId ? 'manual' : (selectedProduct?.id ? 'amazon' : undefined))
      : undefined;

    // キャスティング (settings.model_ids は登場人物A/B 順の正本。キャラクターシートは候補側に入る)
    const frameCasting = buildFrameCasting(selectedCasting, additionalCastings, visualStyleIsStylized);

    // 参照画像の候補 + 抜き差し (§6.1)。hero_image / materials / reference_images は送らず、BE の計画で決める
    await ensureAddedMaterialsResolved(frameSettings.overrides?.added ?? []);
    const referencePlanInput = {
      candidates: collectSceneFrameReferenceCandidates(scenario, scene, 'first', {
        additive: !!options?.additive, added: frameSettings.overrides?.added ?? [],
      }),
      overrides: frameSettings.overrides,
      frame_type: 'first' as const,
    };

    const isAdditive = !!options?.additive;

    try {
      setScenePreviewLoading((prev) => ({ ...prev, [key]: true }));

      // 非同期ジョブを作成（即座にjob_idが返る）
      const { job_id } = await createScenePreviewJob({
        scene_id: scene.id,
        scene: sceneWithIncludeProduct,
        // シーン単位の追加プロンプト (最初のフレームのみ。BE が「# 追加の指示」として末尾に足す)
        additional_prompt: frameAdditionalPrompt || undefined,
        scenario: {
          title: scenario.title,
          scenes: scenario.scenes.map(s => ({ order: s.order, visual: s.visual, setting: s.setting, location_key: s.location_key })),
          locations: resolveScenarioLocations(scenario),
        },
        reference_plan_input: referencePlanInput,
        asin: asinValue,
        product_id: productIdValue,
        product_type: productTypeValue,
        model: scenarioGenerationModel || DEFAULT_GENERATION_MODEL,
        thinking_level: thinkingLevelPayload(scenarioGenerationModel || DEFAULT_GENERATION_MODEL, thinkingLevel),
        aspect_ratio: videoSettings.aspectRatio,
        resolution: videoSettings.resolution,
        prompt_options: {
          tone: tone || undefined,
          duration: duration || undefined,
        },
        // AI俳優のモデルIDを送信（生成メタデータに記録 + 登場人物A/B順の正本）
        settings: frameCasting.settings,
        visual_style: visualStyle,
        shooting_method: shootingMethod,
        avoid_faces: (!visualStyleIsStylized && avoidFaces) || undefined,
        // faceSafe シーン: 絵コンテ用イラストとして生成 (要求値。最終判定は BE §4.2)
        illustration_frame: faceSafeIllustration || undefined,
        // BE の faceSafe 再計算用コンテキスト (§4.2。無ければ scenarios 行へフォールバック)。
        // P2 step 4: シナリオ単位の videoGenerationMode の代わりにシーン単位の実効値
        // (deriveSceneEffectiveVideoGenerationMode) を送る (§9 分類 B と同じ使い分け。
        // video_input_mode が全 NULL の現在は常に同値)。
        video_model: videoSettings.videoModel || undefined,
        video_generation_mode: deriveSceneEffectiveVideoGenerationMode(scenario, scene),
      });

      // クレジット残高を即時更新（バックエンドでデポジット済み）
      refetchCredits();
      if (frameAdditionalPrompt) {
        addToPromptHistory(PROMPT_HISTORY_KEYS.SCENE_FRAME_ADDITIONAL, frameAdditionalPrompt);
      }

      // activeJobsに追加（ステータスバーに表示）
      setActiveJobs(prev => [...prev, {
        jobId: job_id,
        status: 'polling' as const,
        startedAt: Date.now(),
        type: 'scene-preview' as const,
        label: sceneLabel(scenario, scene),
        ...notifyFields,
      }]);

      // ポーリング開始（waitForCompletion時はawaitして完了を待つ）
      const jobPromise = waitForJobCompletion<ScenePreviewResult>(job_id, {
        maxDuration: 20 * 60 * 1000,
        // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
        skipVisibilityCheck: true,
      })
        .then(async (asyncJob) => {
          const data = asyncJob.result_data;

          // activeJobsを完了に更新
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? {
              ...j,
              status: 'completed' as const,
              result: {
                id: data.material_id || job_id,
                material_id: data.material_id,
                image_url: data.image_url,
              },
            } : j
          ));

          // 画像URLをカルーセルに追加（非同期ジョブではimage_urlを使用）
          if (data.image_url) {
            setScenePreviews((prev) => {
              const existing = prev[key] || [];
              const newImages = [...existing, data.image_url!];
              // additiveモード（+1ボタン）では現在の選択を維持
              if (!isAdditive) {
                setScenePreviewIndex(prevIndex => ({ ...prevIndex, [key]: newImages.length - 1 }));
              }
              return { ...prev, [key]: newImages };
            });
          } else {
            console.warn('[ScenePreview] image_url is missing from result_data:', data);
          }

          // scene.image_url / image_material_id を更新。
          // バックエンド (frame_generator._generate_scene_intro) は生成完了時に
          // scenes.image_material_id も書き換えているため、ローカルの scene も同じ値で揃える。
          // ここを image_url だけにしていると、リロードするまで scene.image_material_id が
          // 空のままになり、WorkPanel の動画エディタ CTA (動画かフレーム画像があれば有効) が
          // フレーム生成直後に非活性のまま残る。
          if (data.scene_id && data.image_url) {
            const generatedMaterialId = data.material_id || null;
            setLastResult((prev) => {
              if (!prev) return prev;
              const updatedScenarios = prev.scenarios.map((s) => {
                if (s.id === scenario.id) {
                  const updatedScenes = s.scenes.map((sc) => {
                    if (sc.id === data.scene_id) {
                      return {
                        ...sc,
                        image_url: data.image_url,
                        ...(generatedMaterialId ? { image_material_id: generatedMaterialId } : {}),
                      };
                    }
                    return sc;
                  });
                  return { ...s, scenes: updatedScenes };
                }
                return s;
              });
              return { ...prev, scenarios: updatedScenarios };
            });
          }

          // フレーム生成完了を記録（後続処理でエラーが起きても画像表示は維持）
          setScenePreviewGenerated((prev) => ({ ...prev, [key]: true }));

          // 最初のフレームの画像が表示されたらローディングを即座に解除
          // （後続処理や最後のフレーム生成中にローディング表示が残るのを防ぐ）
          setScenePreviewLoading((prev) => ({ ...prev, [key]: false }));

          // 後続処理（素材登録・前シーンの自動設定等）はエラーが起きても画像表示に影響させない
          try {
            // material情報を保存
            const material = data.material as Record<string, string | number | null | undefined> | undefined;
            if (material) {
              setScenePreviewMaterials((prev) => {
                const existing = prev[key] || [];
                const newMaterials = [...existing, material];
                return { ...prev, [key]: newMaterials };
              });

              const resolvedThumbnailUrl =
                material.thumbnail_url ||
                material.thumbnailUrl ||
                material.preview_url ||
                material.previewUrl;
              const storageBucket = (material.storage_bucket as string) || null;
              const storagePath = (material.storage_path as string) || null;
              const resolvedSourceUrl =
                material.source_url ||
                material.sourceUrl ||
                material.public_url ||
                material.publicUrl ||
                material.preview_url ||
                material.previewUrl ||
                material.temp_path;

              // faceSafe のイラストフレーム (絵コンテ) は選択中素材に入れない。
              // このフレームは人間の構図確認専用で API には渡さない契約
              // (docs/features/frame-mode-seedance-preset-actor-plan.md 第3弾)。選択中素材へ
              // 入れると後続シーンのフレーム生成に「素材画像」として渡り、顔なし写実シーンの
              // フレーム (first_frame として動画モデルへ渡る) にイラスト画風が転写される上、
              // user_selected_asset_ids に永続化されて動画生成の参照画像へも流れ込む
              // (2026-09-10 の混入障害)。判定は BE の最終決定 (generation_metadata.
              // illustration_frame) を正とし、無ければ FE の要求値で補う。
              const materialGenMeta = material.generation_metadata as unknown as
                { illustration_frame?: boolean } | null | undefined;
              const isIllustrationFrame =
                materialGenMeta?.illustration_frame === true || faceSafeIllustration;

              // スコープ指定版を使い、非アクティブシナリオでも正しいスコープに追加
              if (!isIllustrationFrame) {
                addAssetToScope(scenario.id, {
                  id: material.id as string,
                  thumbnailUrl: resolvedThumbnailUrl as string,
                  sourceUrl: resolvedSourceUrl as string,
                  type: 'temporary',
                  storageBucket: storageBucket,
                  storagePath: storagePath,
                  contentType: (material.content_type as string) || null,
                  // 生成フレームの識別子 (BE source_type='scenario')。動画生成の参照画像
                  // 収集 (useVideoHandlers) が isGeneratedSceneFrameAsset で除外するために必要。
                  sourceType: (material.source_type as string) || GENERATED_SCENE_FRAME_SOURCE_TYPE,
                  usage: 'reference',
                  asin: (material.asin as string) || asinValue,
                  width: (material.width as number) || null,
                  height: (material.height as number) || null,
                  aspectRatio: calculateAspectRatio(material.width as number, material.height as number),
                });
              }

              // シーン2以降の場合、前のシーンの最後のフレームが空なら自動設定。
              // faceSafe シーンのフレームはイラスト絵コンテなので、前シーンの i2v の
              // last_frame に流用しない (完成動画の最後のコマがイラストになる事故防止 §4.6)
              if (scene.order > 1 && frameMode === 'first_with_next' && !faceSafeIllustration) {
                const prevScene = scenario.scenes.find(s => s.order === scene.order - 1);
                if (prevScene) {
                  const prevKey = `${scenario.id}-scene-${prevScene.order}`;
                  const prevLastFrame = sceneLastFrames[prevKey];

                  if (!prevLastFrame) {
                    const imageUrl =
                      material.source_url ||
                      material.sourceUrl ||
                      material.preview_url ||
                      material.previewUrl;

                    if (imageUrl) {
                      setSceneLastFrames((prev) => ({
                        ...prev,
                        [prevKey]: {
                          url: imageUrl as string,
                          sourceUrl: imageUrl as string,
                          materialId: (material.id as string) || '',
                        },
                      }));

                      try {
                        await updateSceneMutation.mutateAsync({
                          sceneId: prevScene.id,
                          payload: {
                            last_frame_url: imageUrl as string,
                            last_frame_material_id: (material.id as string) || undefined,
                          },
                        });
                      } catch (error) {
                        console.error('最後のフレームの自動設定に失敗:', error);
                      }
                    }
                  }
                }
              }
            }

            // 「最後のフレームも生成」モードの場合、同一シーンの最後のフレームも生成（エンドカード・商品カットは除く）。
            // faceSafe シーンは first_only 相当として last frame を生成しない (§4.6):
            // イラスト last frame が次の連続シーンの first_frame に流用されると
            // 完成動画の 1 コマ目がイラストになるため。連続シーンへの連鎖は
            // 完成動画から抽出した実写 last_frame (useVideoHandlers) が担う。
            const sceneSetting = scene.setting || '';
            const isEndCardScene = sceneSetting === 'エンドカード' || sceneSetting === '商品カット';
            if (frameMode === 'first_and_last' && !isEndCardScene && !faceSafeIllustration) {
              try {
                // ローディング開始 (最後のフレームのダイアログは自動では開かない。設定済みはボタンの色で分かる。§4.4)
                setLastFrameLoading(prev => ({ ...prev, [key]: true }));

                // 最後のフレーム: 生成直後の最初のフレームを基準に、単独生成と同じ候補関数で参照を組む (§6.2)。
                // 参照画像の調整 (抜き差し) は最後のフレームにも効く (§8-5)
                const firstFrameUrl = data.image_url!;
                const lastReferencePlanInput = {
                  candidates: collectSceneFrameReferenceCandidates(scenario, scene, 'last', {
                    firstFrame: { id: data.material_id || null, url: firstFrameUrl },
                    added: frameSettings.overrides?.added ?? [],
                  }),
                  overrides: frameSettings.overrides,
                  frame_type: 'last' as const,
                };

                const lastFrameJob = await createScenePreviewJob({
                  scene_id: scene.id,
                  scene: sceneWithIncludeProduct,
                  scenario: {
                    title: scenario.title,
                    scenes: scenario.scenes.map(s => ({ order: s.order, visual: s.visual, setting: s.setting, location_key: s.location_key })),
                    locations: resolveScenarioLocations(scenario),
                  },
                  reference_plan_input: lastReferencePlanInput,
                  asin: asinValue,
                  product_id: productIdValue,
                  product_type: productTypeValue,
                  model: scenarioGenerationModel || DEFAULT_GENERATION_MODEL,
                  thinking_level: thinkingLevelPayload(scenarioGenerationModel || DEFAULT_GENERATION_MODEL, thinkingLevel),
                  aspect_ratio: videoSettings.aspectRatio,
                  resolution: videoSettings.resolution,
                  prompt_options: {
                    tone: tone || undefined,
                    duration: duration || undefined,
                  },
                  settings: frameCasting.settings,
                  visual_style: visualStyle,
                  shooting_method: shootingMethod,
                  avoid_faces: (!visualStyleIsStylized && avoidFaces) || undefined,
                  frame_type: 'last',
                });

                refetchCredits();

                setActiveJobs(prev => [...prev, {
                  jobId: lastFrameJob.job_id,
                  status: 'polling' as const,
                  startedAt: Date.now(),
                  type: 'scene-preview' as const,
                  label: sceneLabel(scenario, scene),
                  ...notifyFields,
                }]);

                // 最後のフレーム生成完了を待機
                const lastFramePromise = waitForJobCompletion<ScenePreviewResult>(lastFrameJob.job_id, {
                  maxDuration: 20 * 60 * 1000,
                  // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
                  skipVisibilityCheck: true,
                }).then(async (lastFrameResult) => {
                  const lastFrameData = lastFrameResult.result_data;

                  setActiveJobs(prev => prev.map(j =>
                    j.jobId === lastFrameJob.job_id ? {
                      ...j,
                      status: 'completed' as const,
                      result: {
                        id: lastFrameData.material_id || lastFrameJob.job_id,
                        material_id: lastFrameData.material_id,
                        image_url: lastFrameData.image_url,
                      },
                    } : j
                  ));

                  if (lastFrameData.image_url) {
                    const lastFrameMaterial = lastFrameData.material as Record<string, string | number | null | undefined> | undefined;

                    // サムネイルURLを優先的に使用（高解像度画像の表示を避ける）
                    const lastFrameDisplayUrl = (
                      lastFrameMaterial?.thumbnail_url ||
                      lastFrameMaterial?.thumbnailUrl ||
                      lastFrameMaterial?.preview_url ||
                      lastFrameMaterial?.previewUrl ||
                      lastFrameData.image_url
                    ) as string;

                    // バックエンドに保存用のsource_url
                    const lastFrameSourceUrl = (
                      lastFrameMaterial?.source_url ||
                      lastFrameMaterial?.sourceUrl ||
                      lastFrameData.image_url
                    ) as string;

                    // sceneLastFramesに設定（表示用urlとsourceUrlの両方を保持）
                    setSceneLastFrames((prev) => ({
                      ...prev,
                      [key]: {
                        url: lastFrameDisplayUrl,
                        sourceUrl: lastFrameSourceUrl,
                        materialId: (lastFrameMaterial?.id as string) || '',
                      },
                    }));

                    try {
                      // DBにはサムネイルURLを保存（表示用）。フル解像度はscenario_crud.pyがmaterial_idから解決
                      await updateSceneMutation.mutateAsync({
                        sceneId: scene.id,
                        payload: {
                          last_frame_url: lastFrameDisplayUrl,
                          last_frame_material_id: (lastFrameMaterial?.id as string) || undefined,
                        },
                      });
                    } catch (err) {
                      console.error('最後のフレームの保存に失敗:', err);
                    }

                    // 素材をスコープに追加
                    if (lastFrameMaterial) {
                      const resolvedSource =
                        lastFrameMaterial.source_url ||
                        lastFrameMaterial.sourceUrl ||
                        lastFrameMaterial.temp_path;
                      const resolvedThumb =
                        lastFrameMaterial.thumbnail_url ||
                        lastFrameMaterial.thumbnailUrl ||
                        lastFrameMaterial.preview_url ||
                        lastFrameMaterial.previewUrl;

                      addAssetToScope(scenario.id, {
                        id: lastFrameMaterial.id as string,
                        thumbnailUrl: resolvedThumb as string,
                        sourceUrl: resolvedSource as string,
                        type: 'temporary',
                        storageBucket: (lastFrameMaterial.storage_bucket as string) || null,
                        storagePath: (lastFrameMaterial.storage_path as string) || null,
                        contentType: (lastFrameMaterial.content_type as string) || null,
                        // 生成フレームの識別子 (最初のフレームと同じ。isGeneratedSceneFrameAsset 参照)
                        sourceType: (lastFrameMaterial.source_type as string) || GENERATED_SCENE_FRAME_SOURCE_TYPE,
                        usage: 'reference',
                        asin: (lastFrameMaterial.asin as string) || asinValue,
                        width: (lastFrameMaterial.width as number) || null,
                        height: (lastFrameMaterial.height as number) || null,
                        aspectRatio: calculateAspectRatio(lastFrameMaterial.width as number, lastFrameMaterial.height as number),
                      });
                    }

                    await tempMaterials.refetch();
                    refetchCredits();
                    toast.success(`${sceneLabel(scenario, scene)}の最後のフレームを生成しました`);
                  }
                }).catch((err) => {
                  console.error('[ScenePreview] 最後のフレーム生成に失敗:', err);
                  if (isStorageQuotaError(err)) { toast.error(storageQuotaErrorMessage()); } else { toast.error(`${sceneLabel(scenario, scene)}の最後のフレーム生成に失敗しました`); }
                  setActiveJobs(prev => prev.map(j =>
                    j.jobId === lastFrameJob.job_id ? { ...j, status: 'failed' as const, error: String(err) } : j
                  ));
                  refetchCredits();
                }).finally(() => {
                  setLastFrameLoading(prev => ({ ...prev, [key]: false }));
                });

                // waitForCompletion時は最後のフレーム完了も待つ（一括生成で次シーンが前シーンの最後のフレームを参照するため）
                if (options?.waitForCompletion) {
                  await lastFramePromise;
                }
              } catch (lastFrameError) {
                console.error('[ScenePreview] 最後のフレームジョブ作成に失敗:', lastFrameError);
                toast.error(`${sceneLabel(scenario, scene)}の最後のフレーム生成ジョブの作成に失敗しました`);
                setLastFrameLoading(prev => ({ ...prev, [key]: false }));
              }
            }

            setTimeout(() => {
              setExpandedConfirmButton(key);
            }, 1000);

            await tempMaterials.refetch();
            refetchCredits();
            if (!options?.silent) {
              toast.success(`${sceneLabel(scenario, scene)}の最初のフレームを生成し選択中素材に追加しました`);
            }
            requestOpenPersonalFolder();
          } catch (postError) {
            console.error('[ScenePreview] 後続処理でエラー（画像表示は正常）:', postError);
            if (!options?.silent) {
              toast.success(`${sceneLabel(scenario, scene)}の最初のフレームを生成しました`);
            }
            refetchCredits();
          }
        })
        .catch((error) => {
          console.error('[ScenePreview] Failed to generate scene intro:', error);
          if (isStorageQuotaError(error)) {
            toast.error(storageQuotaErrorMessage());
            refetchCredits();
            setActiveJobs(prev => prev.map(j =>
              j.jobId === job_id ? { ...j, status: 'failed' as const, error: 'STORAGE_QUOTA_EXCEEDED' } : j
            ));
            if (options?.waitForCompletion) throw error;
            return;
          }
          const errorMessage = resolveErrorMessage(error);
          if (!options?.silent) {
            toast.error(`${sceneLabel(scenario, scene)}の最初のフレーム生成に失敗しました: ${errorMessage}`);
          }
          refetchCredits();
          // activeJobsを失敗に更新
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id ? { ...j, status: 'failed' as const, error: errorMessage } : j
          ));
          // waitForCompletion時に呼び出し元でエラーを検知できるよう再throw
          if (options?.waitForCompletion) {
            throw error;
          }
        })
        .finally(() => {
          setScenePreviewLoading((prev) => ({ ...prev, [key]: false }));
        });

      // waitForCompletion が指定された場合、ジョブ完了まで待機
      if (options?.waitForCompletion) {
        await jobPromise;
      }
    } catch (error) {
      // ジョブ作成自体が失敗した場合
      console.error('Failed to create scene preview job', error);
      if (isStorageQuotaError(error)) {
        toast.error(storageQuotaErrorMessage());
        refetchCredits();
        setScenePreviewLoading((prev) => ({ ...prev, [key]: false }));
        if (options?.waitForCompletion) throw error;
        return;
      }
      const errorMessage = resolveErrorMessage(error);
      if (!options?.silent) {
        toast.error(`${sceneLabel(scenario, scene)}の最初のフレーム生成ジョブの作成に失敗しました: ${errorMessage}`);
      }
      refetchCredits();
      setScenePreviewLoading((prev) => ({ ...prev, [key]: false }));
      // waitForCompletion時に呼び出し元でエラーを検知できるよう再throw
      if (options?.waitForCompletion) {
        throw error;
      }
    }
  };

  // 最初のフレームの一括生成
  const handleBatchGenerateFirstFrames = async (
    options?: { scenarios?: Scenario[]; scenarioId?: string }
  ) => {
    const sourceScenarios = options?.scenarios ?? scenarios;
    const currentScenarioId = options?.scenarioId ?? activeScenarioId ?? sourceScenarios[0]?.id;
    const currentScenario = sourceScenarios.find(s => s.id === currentScenarioId);
    if (!currentScenario?.scenes?.length) return;

    // 既にフレーム生成済みのシーン、延長が実効 ON のシーン (§9 step-1 項目5: フレームは
    // 使用されないため一括生成の対象外)、および実効入力方式が 'frame' でないシーン
    // (§3.5: 'auto' 明示シーンのフレーム生成費を先払いさせない。deriveSceneInputFamily は
    // NULL のシーンをシナリオの優先モードへフォールバックするため、優先モードが 'frame' の
    // シナリオでは NULL シーンは従来どおり対象のまま) をスキップ
    const scenesToGenerate = currentScenario.scenes.filter(scene => {
      const key = `${currentScenario.id}-scene-${scene.order}`;
      const materials = scenePreviewMaterials[key] || [];
      if (materials.length > 0) return false;
      if (isFrameLinkPreviousActive(currentScenario, scene)) return false;
      if (deriveSceneInputFamily(currentScenario, scene) !== 'frame') return false;
      return true;
    });

    if (scenesToGenerate.length === 0) {
      toast.success('すべてのシーンに最初のフレームが作成済みです');
      return;
    }

    setBatchGeneratingFrames(prev => ({ ...prev, [currentScenarioId!]: true }));
    batchFrameCancelRefs.current[currentScenarioId!] = false;
    let abortedByError = false;
    // 各フレームジョブの個別完了通知を抑止するための集約通知バッチ。ループ完了後に
    // close する。リロードでバッチが消えた場合は個別通知へフォールバックする。
    const notifyBatchId = openFrameNotifyBatch();
    try {
      for (const scene of scenesToGenerate) {
        if (batchFrameCancelRefs.current[currentScenarioId!]) break;
        const maxRetries = 5;
        let succeeded = false;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          if (batchFrameCancelRefs.current[currentScenarioId!]) break;
          try {
            await handleGenerateScenePreview(currentScenario, scene, { waitForCompletion: true, silent: true, notifyBatchId });
            await new Promise(resolve => setTimeout(resolve, 1000));
            succeeded = true;
            break;
          } catch (error) {
            console.error(`Batch frame generation failed for scene ${scene.order} (attempt ${attempt}/${maxRetries}):`, error);
            if (attempt < maxRetries) {
              const backoffMs = Math.min(2000 * Math.pow(2, attempt - 1), 64000);
              await new Promise(resolve => setTimeout(resolve, backoffMs));
            }
          }
        }
        if (!succeeded && !batchFrameCancelRefs.current[currentScenarioId!]) {
          toast.error(`${sceneLabel(currentScenario, scene)}のフレーム生成に失敗しました。このシナリオの一括生成を中断します。`);
          abortedByError = true;
          break;
        }
      }
      if (batchFrameCancelRefs.current[currentScenarioId!]) {
        toast.show('フレームの一括生成をキャンセルしました');
      } else if (!abortedByError) {
        toast.success('最初のフレームの一括生成が完了しました');
        // 各フレームの個別通知は抑止しているので、全フレーム完了をここで 1 本だけ通知する
        // (フォアグラウンド時は notifyJobComplete 側で抑止され、上の toast のみ表示)。
        notifyJobComplete({
          type: 'scene-preview',
          title: 'フレーム画像の生成が完了しました',
          body: currentScenario.title || undefined,
          jobId: notifyBatchId,
        });
      }
    } finally {
      // ループを抜けたらバッチを閉じる。以降に残るフレームジョブ (通常は無いが、
      // 中断・キャンセル時の取りこぼし等) はバッチ不在となり個別通知へ戻る。
      closeFrameNotifyBatch(notifyBatchId);
      setBatchGeneratingFrames(prev => {
        const next = { ...prev };
        delete next[currentScenarioId!];
        return next;
      });
      delete batchFrameCancelRefs.current[currentScenarioId!];
    }
  };

  // 素材をドロップしてシーンのフレームに設定する
  const handleDropMaterial = async (scenario: Scenario, scene: Scenario['scenes'][number], materialData: DroppedMaterial) => {
    const key = `${scenario.id}-scene-${scene.order}`;

    // ドロップされた素材のURLを取得
    const sourceUrl = materialData.sourceUrl;
    if (!sourceUrl) {
      toast.error('素材のURLが見つかりません');
      return;
    }
    // 表示用URL: thumbnailUrlを優先（フル解像度画像の表示を避ける）
    // (URL 未取得ガードの後に置くことで displayUrl が string に確定する。純粋な式のため挙動は同じ)
    const displayUrl = materialData.thumbnailUrl || sourceUrl;

    // プレビュー画像として追加（表示用URLを使用）
    setScenePreviews((prev) => {
      const existing = prev[key] || [];
      const newImages = [...existing, displayUrl];
      // 最新の画像を表示するためにインデックスを更新
      setScenePreviewIndex(prevIndex => ({ ...prevIndex, [key]: newImages.length - 1 }));
      return { ...prev, [key]: newImages };
    });

    // 素材データも保存
    setScenePreviewMaterials((prev) => {
      const existing = prev[key] || [];
      return { ...prev, [key]: [...existing, materialData] };
    });

    // ドラッグで設定した場合は即座に確定済みにする
    if (materialData.id) {
      await handleConfirmFirstFrame(scene.id, key, materialData.id);
    } else {
      toast.success('フレーム画像を追加しました');
    }
  };

  // 最後のフレーム用のドロップハンドラ
  const handleDropLastFrameMaterial = async (scenario: Scenario, scene: Scenario['scenes'][number], materialData: DroppedMaterial) => {
    const key = `${scenario.id}-scene-${scene.order}`;

    // ドロップされた素材のURLを取得
    const sourceUrl = materialData.sourceUrl;
    if (!sourceUrl) {
      toast.error('元画像のURLが見つかりません。サムネイルは最後のフレームに使用できません。');
      return;
    }
    // 表示用URL: thumbnailUrlまたはsourceUrlを使用（temp_pathなどブラウザで表示不可のパスを避ける）
    // (URL 未取得ガードの後に置くことで displayUrl が string に確定する。純粋な式のため挙動は同じ)
    const displayUrl = materialData.thumbnailUrl || sourceUrl;

    // 最後のフレームを設定
    setSceneLastFrames((prev) => ({
      ...prev,
      [key]: {
        url: displayUrl,
        sourceUrl: sourceUrl,
        materialId: materialData.id || '',
        width: materialData.width,
        height: materialData.height,
      },
    }));

    // バックエンドに保存（サムネイルURLを保存、フル解像度はmaterial_idから解決）
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: {
          last_frame_url: displayUrl,
          last_frame_material_id: materialData.id || undefined,
        },
      });
      toast.success('最後のフレームを設定しました');
    } catch (error) {
      console.error('最後のフレームの保存に失敗:', error);
      toast.error('最後のフレームの保存に失敗しました');
    }
  };

  // 最後のフレームを削除
  const handleRemoveLastFrame = async (scenario: Scenario, scene: Scenario['scenes'][number]) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    const lastFrame = sceneLastFrames[key];

    // selectedAssetsから除去
    if (lastFrame?.materialId) {
      removeGeneratedFrameAssetFromScope(scenario.id, lastFrame.materialId);
    }

    // ローカル状態をクリア
    setSceneLastFrames((prev) => ({
      ...prev,
      [key]: null,
    }));

    // バックエンドに保存
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: {
          last_frame_url: null,
          last_frame_material_id: null,
        },
      });
      toast.success('最後のフレームを削除しました');
    } catch (error) {
      console.error('最後のフレームの削除に失敗:', error);
      toast.error('最後のフレームの削除に失敗しました');
    }
  };

  // 最後のフレームを「開いたときの状態」に戻す (最後のフレームのダイアログの × / Escape)。
  // snapshot = ダイアログを開いたときの sceneLastFrames[key] (null = 未設定)。
  // 今の設定が生成フレームなら選択中素材からも外し (解除と同じ)、snapshot をローカル状態と scenes 行に書き戻す
  const handleRestoreLastFrame = async (
    scenario: Scenario,
    scene: Scenario['scenes'][number],
    snapshot: { url: string; sourceUrl?: string; materialId: string; width?: number; height?: number } | null,
  ) => {
    const key = `${scenario.id}-scene-${scene.order}`;
    const current = sceneLastFrames[key];

    if (current?.materialId && current.materialId !== snapshot?.materialId) {
      removeGeneratedFrameAssetFromScope(scenario.id, current.materialId);
    }

    setSceneLastFrames((prev) => ({
      ...prev,
      [key]: snapshot ? { ...snapshot } : null,
    }));

    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: {
          last_frame_url: snapshot?.url ?? null,
          last_frame_material_id: snapshot?.materialId || null,
        },
      });
      toast.success('最後のフレームを開いたときの状態に戻しました');
    } catch (error) {
      console.error('最後のフレームの復元に失敗:', error);
      toast.error('最後のフレームを戻せませんでした');
    }
  };

  // 最後のフレームを生成する（シーンプレビューの最初のフレームを元に最後のフレームを生成）
  const handleGenerateLastFrame = (scenario: Scenario, scene: Scenario['scenes'][number], sceneKey: string) => {
    const sceneSetting = scene.setting || '';
    const isEndCardScene = sceneSetting === 'エンドカード' || sceneSetting === '商品カット';
    if (isEndCardScene) return;

    // faceSafe シーンは first_only 相当 (§4.6): イラスト絵コンテの last frame は
    // i2v / 連続シーン流用に使えないため手動生成も無効化する (UI 側でも非表示にするが
    // 経路の取りこぼしに備えてここでもガード)。P2 step 4: シナリオ単位の
    // videoGenerationMode の代わりにシーン単位の deriveSceneInputFamily を使う。
    if (isFaceSafeSeedanceScene(
      videoSettings.videoModel, deriveSceneInputFamily(scenario, scene), avoidFaces,
      scenario.scenes[0]?.id === scene.id, scene.continues_previous_shot ?? false,
      scene.has_visible_face, visualStyleIsStylized,
    )) {
      toast.error('このシーンは構図参照用イラストで動画を生成するため、最後のフレームは使用しません');
      return;
    }

    const materials = scenePreviewMaterials[sceneKey] || [];
    const currentIndex = scenePreviewIndex[sceneKey] || 0;
    const currentMaterial = materials[currentIndex];
    const firstFrameUrl = currentMaterial?.source_url || currentMaterial?.sourceUrl || currentMaterial?.preview_url || currentMaterial?.previewUrl;

    if (!firstFrameUrl) {
      toast.error('最初のフレームを先に生成してください');
      return;
    }

    (async () => {
      try {
        setLastFrameLoading(prev => ({ ...prev, [sceneKey]: true }));

        const shouldIncludeProduct = scene.include_product ?? true;
        const frameCastingD = buildFrameCasting(selectedCasting, additionalCastings, visualStyleIsStylized);
        // 参照画像の調整は最後のフレームにも効く (§8-5)。保存済みの最新値を待ってから候補を組む (§6.7)
        const lastFrameSettings = await flushSceneFrameSettings(scene);
        await ensureAddedMaterialsResolved(lastFrameSettings.overrides?.added ?? []);
        const lastReferencePlanInput = {
          candidates: collectSceneFrameReferenceCandidates(scenario, scene, 'last', {
            firstFrame: { id: currentMaterial?.id || null, url: firstFrameUrl },
            added: lastFrameSettings.overrides?.added ?? [],
          }),
          overrides: lastFrameSettings.overrides,
          frame_type: 'last' as const,
        };
        const asin = shouldIncludeProduct ? extractAsinsFromAssets(selectedAssets)[0] : undefined;
        const hasProductAssets = selectedAssets.some(a => a.hasProductLinks);
        const productId = shouldIncludeProduct && hasProductAssets ? (manualProductId || selectedProduct?.id) : undefined;
        const productType = productId ? (manualProductId ? 'manual' : 'amazon') : undefined;

        const lastFrameJob = await createScenePreviewJob({
          scene_id: scene.id,
          scene: { ...scene, include_product: shouldIncludeProduct },
          scenario: {
            title: scenario.title,
            scenes: scenario.scenes.map(s => ({ order: s.order, visual: s.visual, setting: s.setting, location_key: s.location_key })),
            locations: resolveScenarioLocations(scenario),
          },
          reference_plan_input: lastReferencePlanInput,
          asin,
          product_id: productId,
          product_type: productType,
          model: scenarioGenerationModel || DEFAULT_GENERATION_MODEL,
          thinking_level: thinkingLevelPayload(scenarioGenerationModel || DEFAULT_GENERATION_MODEL, thinkingLevel),
          aspect_ratio: videoSettings.aspectRatio,
          resolution: videoSettings.resolution,
          prompt_options: {
            tone: tone || undefined,
            duration: duration || undefined,
          },
          settings: frameCastingD.settings,
          visual_style: visualStyle,
          shooting_method: shootingMethod,
          avoid_faces: (!visualStyleIsStylized && avoidFaces) || undefined,
          frame_type: 'last',
        });

        refetchCredits();
        setActiveJobs(prev => [...prev, {
          jobId: lastFrameJob.job_id,
          status: 'polling' as const,
          startedAt: Date.now(),
          type: 'scene-preview' as const,
          label: sceneLabel(scenario, scene),
        }]);

        waitForJobCompletion<ScenePreviewResult>(lastFrameJob.job_id, {
          maxDuration: 20 * 60 * 1000,
          // 別タブ・最小化中も完了検知を継続しブラウザ通知を出す
          skipVisibilityCheck: true,
        }).then(async (lastFrameResult) => {
          const lastFrameData = lastFrameResult.result_data;
          setActiveJobs(prev => prev.map(j =>
            j.jobId === lastFrameJob.job_id ? { ...j, status: 'completed' as const, result: { id: lastFrameData.material_id || lastFrameJob.job_id, material_id: lastFrameData.material_id, image_url: lastFrameData.image_url } } : j
          ));
          if (lastFrameData.image_url) {
            const lastFrameMaterial = lastFrameData.material as Record<string, string | number | null | undefined> | undefined;
            const displayUrl = (lastFrameMaterial?.thumbnail_url || lastFrameMaterial?.thumbnailUrl || lastFrameMaterial?.preview_url || lastFrameMaterial?.previewUrl || lastFrameData.image_url) as string;
            const sourceUrl = (lastFrameMaterial?.source_url || lastFrameMaterial?.sourceUrl || lastFrameData.image_url) as string;
            setSceneLastFrames(prev => ({ ...prev, [sceneKey]: { url: displayUrl, sourceUrl, materialId: (lastFrameMaterial?.id as string) || '' } }));
            await updateSceneMutation.mutateAsync({ sceneId: scene.id, payload: { last_frame_url: sourceUrl, last_frame_material_id: (lastFrameMaterial?.id as string) || undefined } }).catch(err => console.error('最後のフレームの保存に失敗:', err));
            await tempMaterials.refetch();
            refetchCredits();
            toast.success(`${sceneLabel(scenario, scene)}の最後のフレームを生成しました`);
          }
        }).catch(err => {
          console.error('[ScenePreview] 最後のフレーム生成に失敗:', err);
          if (isStorageQuotaError(err)) { toast.error(storageQuotaErrorMessage()); } else { toast.error(`${sceneLabel(scenario, scene)}の最後のフレーム生成に失敗しました`); }
          setActiveJobs(prev => prev.map(j =>
            j.jobId === lastFrameJob.job_id ? { ...j, status: 'failed' as const, error: String(err) } : j
          ));
          refetchCredits();
        }).finally(() => {
          setLastFrameLoading(prev => ({ ...prev, [sceneKey]: false }));
        });
      } catch (err) {
        console.error('[ScenePreview] 最後のフレームジョブ作成に失敗:', err);
        toast.error(`${sceneLabel(scenario, scene)}の最後のフレーム生成ジョブの作成に失敗しました`);
        setLastFrameLoading(prev => ({ ...prev, [sceneKey]: false }));
      }
    })();
  };

  /** 現在のシナリオの全フレーム（最初のフレーム＋最後のフレーム）をクリアする */
  const handleClearAllFrames = async () => {
    const currentScenario = scenarios.find(s => s.id === activeScenarioId);
    if (!currentScenario?.scenes?.length) return;

    try {
      for (const scene of currentScenario.scenes) {
        const sceneKey = `${currentScenario.id}-scene-${scene.order}`;

        // 最初のフレーム: 選択中素材を除外
        const materialsForScene = scenePreviewMaterials[sceneKey] || [];
        materialsForScene.forEach((m: any) => {
          if (m?.id) removeGeneratedFrameAsset(m.id);
        });

        // 最後のフレーム: 選択中素材を除外
        const lastFrame = sceneLastFrames[sceneKey];
        if (lastFrame?.materialId) {
          removeGeneratedFrameAssetFromScope(currentScenario.id, lastFrame.materialId);
        }

        // バックエンドに保存
        await updateSceneMutation.mutateAsync({
          sceneId: scene.id,
          payload: {
            image_material_id: undefined,
            last_frame_url: null,
            last_frame_material_id: null,
          },
        });
      }

      // ローカル状態を一括クリア
      const sceneKeys = currentScenario.scenes.map(s => `${currentScenario.id}-scene-${s.order}`);
      const deleteKeys = <T,>(setter: React.Dispatch<React.SetStateAction<Record<string, T>>>) => {
        setter(prev => {
          const next = { ...prev };
          sceneKeys.forEach(k => delete next[k]);
          return next;
        });
      };

      deleteKeys(setScenePreviews);
      deleteKeys(setScenePreviewIndex);
      deleteKeys(setScenePreviewMaterials);
      deleteKeys(setConfirmedFirstFrames);
      deleteKeys(setSceneLastFrames);

      // lastResult のシナリオデータも更新
      setLastResult(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          scenarios: prev.scenarios.map(s => {
            if (s.id !== currentScenario.id) return s;
            return {
              ...s,
              scenes: s.scenes.map(sc => ({
                ...sc,
                image_material_id: undefined,
                last_frame_url: undefined,
                last_frame_material_id: undefined,
              })),
            };
          }),
        };
      });

      toast.success('すべてのフレームをクリアしました');
    } catch (error) {
      console.error('Failed to clear all frames:', error);
      toast.error('フレームのクリアに失敗しました');
    }
  };

  return {
    // State
    batchGeneratingFrames,
    setBatchGeneratingFrames,
    batchFrameCancelRefs,
    showBatchCancelModal,
    setShowBatchCancelModal,

    // Handlers
    handleGenerateScenePreview,
    collectSceneFrameReferenceCandidates,
    ensureAddedMaterialsResolved,
    forgetAddedMaterial,
    handleConfirmFirstFrame,
    handleClearFirstFrame,
    handleClearAllFrames,
    handleDropMaterial,
    handleDropLastFrameMaterial,
    handleRemoveLastFrame,
    handleRestoreLastFrame,
    handleGenerateLastFrame,
    handleBatchGenerateFirstFrames,
  };
}
